import { Item } from '../../domain/item';
import { recordKey } from '../collection/multiCollectionIdentity';
import { captureActivityPresentation } from './captureActivityPresentation';
import {
  captureDraftFields,
  capturePhoto,
  captureDraftMergeKeywords,
  RapidCaptureRecord,
} from './rapidCaptureTypes';
import { storagePaths } from '@qkindex/contracts';

/** Local projections never enter the authoritative inventory store or domain writers. */
export function projectCaptureInventory({
  records,
  items,
  authority,
  images,
  online,
  openQueue,
  hold,
}: {
  records: RapidCaptureRecord[];
  items: Item[];
  authority?: {
    userId: string;
    collectionId: string;
    membershipVersion: number;
    lifecycleVersion?: number;
  };
  images: ReadonlyMap<string, string>;
  online: boolean;
  openQueue: () => void;
  hold?: (operationId: string, open: boolean) => Promise<void>;
}): Item[] {
  if (!authority) return [];
  const byId = new Map(items.map((item) => [recordKey(item), item]));
  return records.flatMap((record) => {
    if (
      record.schemaVersion !== 6 ||
      record.state === 'terminal' ||
      record.itemUnavailable ||
      record.userId !== authority.userId ||
      record.collectionId !== authority.collectionId ||
      record.membershipVersion !== authority.membershipVersion ||
      record.lifecycleVersion !== authority.lifecycleVersion
    )
      return [];
    const item = byId.get(
      recordKey({ id: record.itemId, collectionId: record.collectionId }),
    );
    if (
      (record.mutation && !item) ||
      (item &&
        (item.deleted ||
          (!record.mutation && item.captureOperationId !== record.operationId)))
    )
      return [];
    const activity = captureActivityPresentation(record, online, item);
    const photo = capturePhoto(record);
    const photoPath =
      photo?.image === null
        ? ''
        : photo?.operationId
          ? storagePaths.durableImage(
              record.collectionId,
              `${photo.operationId}.jpg`,
            )
          : undefined;
    const photoApplies =
      !photo?.committed ||
      !item ||
      item.storagePath === photoPath ||
      (item.revision ?? Infinity) < (record.itemRevision ?? 0);
    // A metadata-only edit must keep the completed capture's exact photo URL.
    const retainedImage =
      !photo && item?.storagePath
        ? images.get(`item:${item.id}:${item.storagePath}`)
        : undefined;
    const captureImage =
      (photo && photoApplies ? images.get(record.operationId) : undefined) ||
      retainedImage;
    const localPhotoVisible =
      Boolean(captureImage) || (photoApplies && photo?.image === null);
    return [
      {
        ...(item || {
          id: record.itemId,
          collectionId: record.collectionId,
          createdAt: record.createdAt,
          image: '',
          tagIds: record.tagIds,
        }),
        ...captureDraftFields(record, item),
        image:
          photoApplies && photo?.image === null
            ? ''
            : captureImage || item?.image || '',
        ...(record.publishedStoragePath === undefined ||
        (item?.revision ?? -1) > (record.publishedRevision ?? -1)
          ? {}
          : { storagePath: record.publishedStoragePath }),
        imageLoading: localPhotoVisible ? false : item?.imageLoading,
        requestImage: localPhotoVisible ? undefined : item?.requestImage,
        captureOperationId: record.mutation
          ? item?.captureOperationId
          : record.operationId,
        localCapture: {
          operationId: record.operationId,
          published: record.itemCreated || Boolean(record.mutation),
          ...(activity.severity === 'info'
            ? {}
            : { attention: activity.severity }),
          openQueue,
          hold: hold && ((open: boolean) => hold(record.operationId, open)),
          mergeKeywords: captureDraftMergeKeywords(record, item),
          authorityKey: `${authority.userId}:${authority.membershipVersion}:${authority.lifecycleVersion}`,
          ...(record.lastError?.phase === 'edits' &&
          record.lastError.category === 'conflict' &&
          item
            ? { conflict: item }
            : {}),
        },
      },
    ];
  });
}

/** One collection-qualified identity survives local acceptance and server publication. */
export function mergeCaptureInventory(items: Item[], drafts: Item[]): Item[] {
  const merged = new Map(items.map((item) => [recordKey(item), item]));
  for (const draft of drafts) {
    const previous = merged.get(recordKey(draft));
    if (
      !draft.localCapture &&
      previous &&
      (previous.revision ?? -1) > (draft.revision ?? -1)
    )
      continue;
    // A pushed metadata snapshot does not replace the scoped image reader.
    const reuseImage =
      previous &&
      !draft.localCapture &&
      !draft.image &&
      !draft.deleted &&
      previous.storagePath === draft.storagePath;
    merged.set(
      recordKey(draft),
      reuseImage
        ? {
            ...draft,
            image: previous.image,
            imageLoading: previous.imageLoading,
            requestImage: previous.requestImage,
          }
        : draft,
    );
  }
  return [...merged.values()];
}
